import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  type AssistantRuntime,
  type ChatModelAdapter,
  type ThreadHistoryAdapter,
  useAui,
  useAuiState,
  useLocalRuntime,
} from '@assistant-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import { type AndiApi, useAndiAPIFor } from '../api/andiClient';
import type { StreamFrame } from '../api/types';
import { isReplyInFlight, isUnanswered } from '../chatState';
import { CHAT_REFRESH_DEBOUNCE_MS } from '../constants';
import { getChatQueryObject, refreshChat } from '../hooks/useChatQuery';
import { useLatest } from '../hooks/useLatest';

import { clarifiedEvent, pickedValue } from './choice';
import { applyFrame, draftResult, followReply, newDraft } from './replyStream';
import { toRepository } from './transcript';

/** How long a new chat's first message waits for the chat to be created. */
const CHAT_CREATED_TIMEOUT_MS = 30_000;

const textOf = (content: readonly { type: string; text?: string }[]) => (
  content.filter((part) => part.type === 'text').map((part) => part.text ?? '').join('').trim()
);

/**
 * The runtime for one chat, kept by the chat list's runtime (see AndiRuntimeProvider). assistant-ui's
 * LocalRuntime owns the messages and the reply in progress; this supplies the two things it can't know:
 * how to stream a reply (the chat adapter) and how to load a chat (the history adapter). Each message
 * carries our item on `metadata.custom.item` (see messageItem).
 *
 * `projectId` comes from the chat list's runtime rather than the route: see AndiRuntimeProvider.
 */
export function useChatThreadRuntime(projectId: string | undefined): AssistantRuntime {
  const getAPI = useAndiAPIFor(projectId);
  const getAPIRef = useLatest(getAPI);
  const projectIdRef = useLatest(projectId);
  const aui = useAui();
  const auiRef = useLatest(aui);
  const queryClient = useQueryClient();

  const chatQuery = useCallback(
    (chatId: string) => getChatQueryObject(projectIdRef.current, chatId, getAPIRef.current),
    [getAPIRef, projectIdRef],
  );
  const latestChat = useCallback(
    (chatId: string) => queryClient.fetchQuery({ ...chatQuery(chatId), staleTime: 0 }).catch(() => null),
    [chatQuery, queryClient],
  );

  // A reply's activity changes the chat (its plan, its provider): refresh it, once per burst.
  const refreshTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const onFrame = useCallback((chatId: string, frame: StreamFrame) => {
    if (frame.type !== 'event') return;
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(
      () => void refreshChat(queryClient, projectIdRef.current, chatId),
      CHAT_REFRESH_DEBOUNCE_MS,
    );
  }, [projectIdRef, queryClient]);

  const framesOf = useCallback(async function* frames(
    chatId: string,
    open: (api: AndiApi) => AsyncIterable<StreamFrame>,
  ): AsyncGenerator<StreamFrame> {
    for await (const frame of open(await getAPIRef.current())) {
      onFrame(chatId, frame);
      yield frame;
    }
  }, [getAPIRef, onFrame]);

  const follow = useCallback((
    chatId: string,
    prompt: string,
    streamId: string | null,
    stop: () => boolean,
  ) => followReply({
    prompt,
    streamId,
    stop,
    resume: (id) => framesOf(chatId, (api) => api.resumeReply(chatId, id)),
    latestChat: () => latestChat(chatId),
    onEnded: () => void refreshChat(queryClient, projectIdRef.current, chatId),
  }), [framesOf, latestChat, projectIdRef, queryClient]);

  // This chat's id: undefined for a new chat until the chat list creates it.
  const remoteId = useAuiState((state) => state.optional.threadListItem?.remoteId);
  const remoteIdRef = useLatest(remoteId);

  // A new chat's first message is sent as the chat is being created: wait for its id.
  const chatIdWhenCreated = useCallback((): Promise<string> => {
    const read = () => auiRef.current.optional.threadListItem?.getState().remoteId;
    const existing = read();
    if (existing) return Promise.resolve(existing);
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        unsubscribe();
        reject(new Error('Timed out waiting for the chat to be created'));
      }, CHAT_CREATED_TIMEOUT_MS);
      const unsubscribe = auiRef.current.subscribe(() => {
        const id = read();
        if (!id) return;
        clearTimeout(timer);
        unsubscribe();
        resolve(id);
      });
    });
  }, [auiRef]);

  // Loading a chat draws it from the cached state at once (a chat already seen, or prefetched from the list),
  // then refreshes it. When the chat ends on a reply still being written, the effect below follows it, on the
  // stream the chat names.
  const followOnLoad = useRef<{ streamId: string | null } | null>(null);
  const history = useMemo<ThreadHistoryAdapter>(() => ({
    async load() {
      const chatId = remoteIdRef.current;
      if (!chatId) return { messages: [] };
      const query = chatQuery(chatId);
      const cached = queryClient.getQueryData(query.queryKey);
      const chat = cached ?? await queryClient.fetchQuery(query);
      if (cached) void refreshChat(queryClient, projectIdRef.current, chatId);
      followOnLoad.current = isReplyInFlight(chat) ? { streamId: chat.turn?.stream_id ?? null } : null;
      return toRepository(chat);
    },
    // The server saves each message as the reply runs.
    async append() {},
  }), [chatQuery, projectIdRef, queryClient, remoteIdRef]);

  // Sends the builder's message and streams the reply. A message the chat already holds unanswered (a new
  // chat's opening message, or the one "Send it again" retries) isn't sent again: the reply answers it. A
  // stream that drops before the reply ends is followed: the server is still writing it.
  const chatAdapter = useMemo<ChatModelAdapter>(() => ({
    async* run({ messages, runConfig, abortSignal }) {
      const retryText = (runConfig?.custom as { retryText?: string } | undefined)?.retryText;
      const lastUser = messages.findLast((message) => message.role === 'user');
      const text = retryText || (lastUser ? textOf(lastUser.content) : '');
      if (!text) return;
      const chatId = await chatIdWhenCreated();
      // The server saves a pick as a "Clarified" event but doesn't stream it: show it at once.
      const picked = pickedValue(text);
      const draft = newDraft(picked === null ? [] : [clarifiedEvent(picked)]);
      yield draftResult(draft, true, text);
      const held = (Boolean(retryText) || messages.length === 1)
        && await latestChat(chatId).then((chat) => chat !== null && isUnanswered(chat));
      try {
        const frames = framesOf(
          chatId,
          (api) => api.streamReply(chatId, held ? null : text, abortSignal),
        );
        for await (const frame of frames) {
          applyFrame(draft, frame);
          yield draftResult(draft, true, text);
        }
      } catch {
        // The connection dropped mid-reply: followed below.
      }
      if (abortSignal.aborted) return;
      if (draft.done) yield draftResult(draft, false, text);
      else if (draft.broke) yield draftResult(draft, false, text, true);
      else yield* follow(chatId, text, draft.streamId, () => abortSignal.aborted);
      // The reply ended: refresh the chat and the chat list (the chat's name can change with it).
      void refreshChat(queryClient, projectIdRef.current, chatId);
      void auiRef.current.threads.reload().catch(() => {});
    },
  }), [auiRef, chatIdWhenCreated, follow, framesOf, latestChat, projectIdRef, queryClient]);

  const runtime = useLocalRuntime(chatAdapter, { adapters: { history } });
  const runtimeRef = useLatest(runtime);

  // After a refresh mid-reply: once the chat's history is drawn, reattach to the reply and follow it, the
  // same way a dropped stream is followed.
  useEffect(() => {
    const chatId = remoteIdRef.current;
    if (!chatId) return undefined;
    let cancelled = false;
    const reattach = () => {
      const thread = runtimeRef.current.thread.getState();
      const pending = followOnLoad.current;
      if (cancelled || !pending || thread.isLoading || thread.isRunning) return;
      followOnLoad.current = null;
      try {
        runtimeRef.current.thread.resumeRun({
          parentId: thread.messages.at(-1)?.id ?? null,
          stream: () => follow(chatId, '', pending.streamId, () => cancelled),
        });
      } catch {
        // The chat then shows its last saved state.
      }
    };
    reattach();
    const unsubscribe = runtimeRef.current.thread.subscribe(reattach);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [follow, remoteId, remoteIdRef, runtimeRef]);

  return runtime;
}
